<template>
  <PageShell title="Sales" subtitle="Track revenue • COGS • shipments • profit heat">
    <!-- tiny toast -->
    <div v-if="toast" class="toast">{{ toast }}</div>

    <template #actions>
      <button class="ll-pill ghost" type="button" @click="openWizard = true">
        Quick Add Wizard
      </button>

      <button class="ll-pill" type="button" @click="refresh" :disabled="loading">
        {{ loading ? "Refreshing..." : "Refresh" }}
      </button>
    </template>

    <div class="layout">
      <!-- Filters + range + snapshot -->
      <section class="card">
        <div class="card-top">
          <div>
            <div class="card-title">Filters</div>
            <div class="micro subtle">
              Tip: expand a sale to lazy-load lines. Use <b>Only Losses</b> to find leaks fast.
            </div>
          </div>

          <div class="micro dim">
            Showing <b>{{ fmtInt(sortedSales.length) }}</b> of <b>{{ fmtInt(sales.length) }}</b>
          </div>
        </div>

        <div class="filters">
          <input
            class="input"
            type="text"
            v-model="filters.search"
            placeholder="Search platform, invoice, item, description…"
            @input="onFilterChange"
          />

          <select class="input" v-model="filters.platform" @change="onFilterChange">
            <option value="">All Platforms</option>
            <option v-for="p in platforms" :key="p" :value="p">{{ p }}</option>
          </select>

          <select class="input" v-model="filters.shipped" @change="onFilterChange">
            <option value="">All Status</option>
            <option value="Y">Shipped</option>
            <option value="N">Not Shipped</option>
          </select>

          <label class="toggle">
            <input type="checkbox" v-model="useItemTracking" />
            Use Per-Item Tracking
          </label>

          <!-- ✅ Only Losses -->
          <label class="toggle danger">
            <input type="checkbox" v-model="filters.only_losses" />
            Only Losses
          </label>
        </div>

        <!-- ✅ Range chips -->
        <div class="range-row">
          <button
            v-for="r in ranges"
            :key="r.key"
            class="range-chip"
            type="button"
            :class="filters.range === r.key ? 'on' : ''"
            @click="setRange(r.key)"
            :title="r.hint"
          >
            {{ r.label }}
          </button>

          <div class="range-spacer"></div>

          <div class="snapshot">
            <div class="snap">
              <div class="k">Revenue</div>
              <div class="v mono">${{ fmtMoney(snapshot.revenue) }}</div>
            </div>
            <div class="snap">
              <div class="k">COGS</div>
              <div class="v mono">${{ fmtMoney(snapshot.cogs) }}</div>
            </div>
            <div class="snap">
              <div class="k">Net</div>
              <div class="v mono">${{ fmtMoney(snapshot.net) }}</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Empty state -->
      <section v-if="!loading && sortedSales.length === 0" class="card empty">
        <div class="empty-title">No sales found</div>
        <div class="empty-sub">
          Try widening the range, clearing filters, or add one manually.
        </div>

        <div class="empty-actions">
          <button class="ll-pill ghost" type="button" @click="clearFilters">
            Clear Filters
          </button>
          <button class="ll-pill" type="button" @click="openWizard = true">
            Quick Add
          </button>
        </div>
      </section>

      <!-- Desktop table -->
      <section v-if="!isMobile && sortedSales.length" class="card">
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th class="expander-col"></th>
                <th>Sale</th>
                <th>Date</th>
                <th>Total</th>
                <th>COGS</th>
                <th>Net</th>
                <th>Heat</th>
                <th>Invoice</th>
                <th>Track</th>
                <th>Shipped</th>
              </tr>
            </thead>

            <tbody>
              <template v-for="s in sortedSales" :key="s.sale_id">
                <tr class="sale-row">
                  <td class="expander" @click="toggleExpand(s.sale_id, s.sale_date)">
                    <span v-if="isExpanded(s.sale_id)">▼</span>
                    <span v-else>▶</span>
                  </td>

                  <td>
                    <div class="sale-title">
                      <span class="strong mono">#{{ s.sale_id }}</span>

                      <span class="chip sm" v-if="s.platform">{{ s.platform }}</span>
                      <span class="chip sm ghost" v-else>— platform</span>

                      <span class="badge" :class="s.shipped_flag === 'Y' ? 'ok' : 'no'">
                        {{ s.shipped_flag === 'Y' ? "Shipped" : "Not Shipped" }}
                      </span>

                      <button
                        class="mini ghost"
                        type="button"
                        title="Copy sale id"
                        @click.stop="copyText(String(s.sale_id), 'Sale # copied')"
                      >
                        Copy #
                      </button>

                      <button
                        v-if="s.invoice_number"
                        class="mini ghost"
                        type="button"
                        title="Copy invoice number"
                        @click.stop="copyText(s.invoice_number, 'Invoice copied')"
                      >
                        Copy Inv
                      </button>
                    </div>

                    <div class="micro subtle">
                      <span v-if="s.invoice_number" class="dim">Inv {{ s.invoice_number }}</span>
                      <span v-if="s.buyer_name" class="dim">• {{ s.buyer_name }}</span>
                      <span v-if="s.tracking_number && !useItemTracking" class="dim">• Tracking on header</span>
                    </div>
                  </td>

                  <td class="mono">{{ fmtDate(s.sale_date) }}</td>
                  <td class="mono">${{ fmtMoney(s.total_sale) }}</td>
                  <td class="mono">${{ fmtMoney(s.total_cogs) }}</td>
                  <td class="mono" :class="Number(s.net_profit || 0) < 0 ? 'neg' : ''">
                    ${{ fmtMoney(s.net_profit) }}
                  </td>

                  <td>
                    <span class="heat" :class="profitHeat(s).cls" :title="profitHeat(s).title">
                      {{ profitHeat(s).label }}
                    </span>
                  </td>

                  <td>
                    <span v-if="s.invoice_number" class="mono">{{ s.invoice_number }}</span>
                    <span v-else class="muted">—</span>
                  </td>

                  <td>
                    <a
                      v-if="!useItemTracking && s.tracking_number"
                      href="#"
                      @click.prevent="openTracking(s.tracking_number, s.carrier)"
                      class="track-link"
                    >
                      Track
                    </a>
                    <span v-else class="muted">—</span>
                  </td>

                  <td class="center">
                    <input type="checkbox" :checked="String(s.shipped_flag) === 'Y'" disabled />
                  </td>
                </tr>

                <!-- Expanded lines -->
                <tr v-if="isExpanded(s.sale_id)">
                  <td colspan="10">
                    <div class="lines">
                      <!-- ✅ skeleton while loading -->
                      <div v-if="linesLoading[s.sale_id]" class="lines-inner">
                        <div v-for="n in 3" :key="n" class="line-row skeleton">
                          <div class="sk-left">
                            <div class="sk-bar w60"></div>
                            <div class="sk-bar w45"></div>
                          </div>
                          <div class="sk-right">
                            <div class="sk-bar w70"></div>
                          </div>
                        </div>
                      </div>

                      <div v-else-if="saleLines[s.sale_id]?.length" class="lines-inner">
                        <div
                          v-for="ln in saleLines[s.sale_id]"
                          :key="ln.sale_detail_id"
                          class="line-row"
                        >
                          <div class="line-left">
                            <div class="line-title">
                              {{ ln.item_name || "—" }}
                              <span class="muted">• {{ ln.item_no || "—" }}</span>
                            </div>

                            <div class="line-sub">
                              Qty: <strong class="mono">{{ fmtInt(ln.quantity_sold) }}</strong>
                              <span class="dot">•</span>
                              Rev: <strong class="mono">${{ fmtMoney(ln.total_revenue) }}</strong>
                              <span class="dot">•</span>
                              COGS: <strong class="mono">${{ fmtMoney(ln.cogs) }}</strong>
                              <span class="dot">•</span>
                              <strong>{{ String(ln.shipped_flag) === "Y" ? "Shipped" : "Not shipped" }}</strong>
                            </div>
                          </div>

                          <div class="line-right">
                            <a
                              v-if="useItemTracking && ln.tracking_number && ln.carrier"
                              href="#"
                              class="mono track-link"
                              @click.prevent="openTracking(ln.tracking_number, ln.carrier)"
                            >
                              {{ ln.tracking_number }}
                            </a>
                            <span v-else class="muted">—</span>
                          </div>
                        </div>
                      </div>

                      <div v-else class="muted pad">No line items found.</div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Mobile cards -->
      <section v-if="isMobile && sortedSales.length" class="mobile-only">
        <div class="mobile-list">
          <article
            v-for="s in sortedSales"
            :key="s.sale_id"
            class="row-card compact"
            @click="toggleExpand(s.sale_id, s.sale_date)"
          >
            <div class="row-head">
              <div class="row-left">
                <div class="sale-title">
                  <span class="strong mono">#{{ s.sale_id }}</span>

                  <span class="heat" :class="profitHeat(s).cls" :title="profitHeat(s).title" style="margin-left:6px;">
                    {{ profitHeat(s).label }}
                  </span>

                  <span class="badge" :class="s.shipped_flag === 'Y' ? 'ok' : 'no'" style="margin-left:6px;">
                    {{ s.shipped_flag === 'Y' ? "Shipped" : "Not Shipped" }}
                  </span>
                </div>

                <div class="micro subtle">
                  <span v-if="s.platform">{{ s.platform }}</span>
                  <span v-else>—</span>
                  <span class="dim"> • {{ fmtDate(s.sale_date) }}</span>
                  <span v-if="s.invoice_number" class="dim"> • Inv {{ s.invoice_number }}</span>
                </div>
              </div>

              <div class="chev">
                <span v-if="isExpanded(s.sale_id)">▼</span>
                <span v-else>▶</span>
              </div>
            </div>

            <div class="sale-metrics">
              <div class="metric">
                <div class="k">Total</div>
                <div class="v mono">${{ fmtMoney(s.total_sale) }}</div>
              </div>
              <div class="metric">
                <div class="k">COGS</div>
                <div class="v mono">${{ fmtMoney(s.total_cogs) }}</div>
              </div>
              <div class="metric">
                <div class="k">Net</div>
                <div class="v mono" :class="Number(s.net_profit || 0) < 0 ? 'neg' : ''">
                  ${{ fmtMoney(s.net_profit) }}
                </div>
              </div>
            </div>

            <div class="btn-row" @click.stop>
              <button
                class="mini ghost"
                type="button"
                @click.stop="copyText(String(s.sale_id), 'Sale # copied')"
              >
                Copy #
              </button>

              <button
                v-if="s.invoice_number"
                class="mini ghost"
                type="button"
                @click.stop="copyText(s.invoice_number, 'Invoice copied')"
              >
                Copy Inv
              </button>

              <button
                v-if="!useItemTracking && s.tracking_number"
                class="mini"
                type="button"
                @click.stop="openTracking(s.tracking_number, s.carrier)"
              >
                Track
              </button>
            </div>

            <div v-if="isExpanded(s.sale_id)" class="sale-lines" @click.stop>
              <!-- ✅ skeleton while loading -->
              <div v-if="linesLoading[s.sale_id]" class="lines-inner">
                <div v-for="n in 2" :key="n" class="line-row skeleton">
                  <div class="sk-left">
                    <div class="sk-bar w60"></div>
                    <div class="sk-bar w45"></div>
                  </div>
                  <div class="sk-right">
                    <div class="sk-bar w70"></div>
                  </div>
                </div>
              </div>

              <div v-else-if="saleLines[s.sale_id]?.length">
                <div
                  v-for="ln in saleLines[s.sale_id]"
                  :key="ln.sale_detail_id"
                  class="sale-line"
                >
                  <div class="ln-title">
                    {{ ln.item_name || "—" }}
                    <span class="muted">• {{ ln.item_no || "—" }}</span>
                  </div>

                  <div class="ln-sub">
                    Qty <strong class="mono">{{ fmtInt(ln.quantity_sold) }}</strong>
                    <span class="dot">•</span>
                    $<strong class="mono">{{ fmtMoney(ln.total_revenue) }}</strong>
                    <span class="dot">•</span>
                    {{ String(ln.shipped_flag) === "Y" ? "Shipped" : "Not shipped" }}
                  </div>

                  <div class="ln-track">
                    <a
                      v-if="useItemTracking && ln.tracking_number && ln.carrier"
                      href="#"
                      class="mono track-link"
                      @click.prevent="openTracking(ln.tracking_number, ln.carrier)"
                    >
                      {{ ln.tracking_number }}
                    </a>
                    <span v-else class="muted">—</span>
                  </div>
                </div>
              </div>

              <div v-else class="muted">No line items found.</div>
            </div>
          </article>
        </div>
      </section>
    </div>

    <!-- Wizard Modal -->
    <div v-if="openWizard" class="modal">
      <div class="modal-body">
        <SalesAssistantMode @exit="openWizard = false" @review="onWizardReview" />
      </div>
    </div>

    <!-- Draft Review Modal -->
    <div v-if="reviewOpen" class="modal" @click.self="reviewOpen = false">
      <div class="review">
        <div class="review-head">
          <div class="review-title">Review Sale Draft</div>
          <button class="ll-pill ghost" type="button" @click="reviewOpen = false">Close</button>
        </div>

        <div class="review-grid">
          <div class="review-box">
            <div class="label">Platform</div>
            <div class="value">{{ wizardDraft.platform || "—" }}</div>
          </div>
          <div class="review-box">
            <div class="label">Sale Date</div>
            <div class="value">{{ wizardDraft.sale_date || "—" }}</div>
          </div>
          <div class="review-box">
            <div class="label">Buyer</div>
            <div class="value">{{ wizardDraft.buyer_name || "—" }}</div>
          </div>
          <div class="review-box">
            <div class="label">Fees / Taxes / Freight</div>
            <div class="value mono">
              ${{ fmtMoney(wizardDraft.total_fees) }} /
              ${{ fmtMoney(wizardDraft.total_taxes) }} /
              ${{ fmtMoney(wizardDraft.total_freight) }}
            </div>
          </div>
        </div>

        <div class="review-lines">
          <div class="review-lines-title">Lines</div>

          <div
            v-if="Array.isArray(wizardDraft.items) && wizardDraft.items.length"
            class="review-lines-list"
          >
            <div v-for="(it, idx) in wizardDraft.items" :key="idx" class="review-line">
              <div class="ln-title">
                {{ it.item_name || "—" }}
                <span class="muted">• {{ it.item_no || "—" }}</span>
              </div>
              <div class="ln-sub mono">
                Qty {{ fmtInt(it.quantity_sold ?? it.quantity ?? 0) }}
                • Unit ${{ fmtMoney(it.unit_price ?? 0) }}
                • Total ${{ fmtMoney(Number(it.quantity_sold ?? it.quantity ?? 0) * Number(it.unit_price ?? 0)) }}
              </div>
            </div>
          </div>

          <div v-else class="muted">No lines in draft.</div>
        </div>

        <div class="review-actions">
          <button class="ll-pill ghost" type="button" @click="reviewOpen = false">Cancel</button>
          <button class="ll-pill" type="button" :disabled="savingDraft" @click="saveWizardDraft">
            {{ savingDraft ? "Saving…" : "Save Sale" }}
          </button>
        </div>
      </div>
    </div>
  </PageShell>
</template>

<script>
import api from "@/services/api";
import PageShell from "@/components/layout/PageShell.vue";
import { openExternal } from "@/utils/openExternal";
import SalesAssistantMode from "@/components/addInventory/SalesAssistantMode.vue";

export default {
  name: "SalesPage",
  components: { PageShell, SalesAssistantMode },

  data() {
    return {
      loading: false,
      sales: [],
      saleLines: {},       // { [sale_id]: lines[] }
      linesLoading: {},    // { [sale_id]: true/false }
      expanded: new Set(),
      useItemTracking: false,

      platforms: [],
      filters: {
        search: "",
        platform: "",
        shipped: "",
        range: "30d",
        only_losses: false, // ✅ Only Losses
      },

      ranges: [
        { key: "7d",  label: "7D",  hint: "Last 7 days" },
        { key: "30d", label: "30D", hint: "Last 30 days" },
        { key: "90d", label: "90D", hint: "Last 90 days" },
        { key: "ytd", label: "YTD", hint: "Year to date" },
        { key: "all", label: "ALL", hint: "All time" },
      ],

      openWizard: false,
      reviewOpen: false,
      wizardDraft: {},
      savingDraft: false,

      toast: "",

      isMobile: false,
      mq: null,
    };
  },

  computed: {
    filteredSales() {
      const q = String(this.filters.search || "").trim().toLowerCase();

      const inRange = (saleDate) => {
        if (!saleDate) return true;
        if (this.filters.range === "all") return true;

        const d = new Date(saleDate);
        if (Number.isNaN(d.getTime())) return true;

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        if (this.filters.range === "ytd") {
          const start = new Date(now.getFullYear(), 0, 1);
          return d >= start && d <= startOfToday;
        }

        const days =
          this.filters.range === "7d" ? 7 :
          this.filters.range === "30d" ? 30 :
          this.filters.range === "90d" ? 90 : 365;

        const start = new Date(startOfToday);
        start.setDate(start.getDate() - (days - 1));
        return d >= start && d <= startOfToday;
      };

      return (this.sales || []).filter((s) => {
        if (!inRange(s.sale_date)) return false;

        if (this.filters.platform && s.platform !== this.filters.platform) return false;
        if (this.filters.shipped && String(s.shipped_flag) !== this.filters.shipped) return false;

        // ✅ Only losses
        if (this.filters.only_losses) {
          const net = Number(s?.net_profit || 0);
          if (!(net < 0)) return false;
        }

        if (!q) return true;

        const headerHit =
          String(s.platform || "").toLowerCase().includes(q) ||
          String(s.sale_id || "").includes(q) ||
          String(s.lot_number || "").toLowerCase().includes(q) ||
          String(s.invoice_number || "").toLowerCase().includes(q) ||
          String(s.buyer_name || "").toLowerCase().includes(q);

        // include line search if already fetched
        const lines = this.saleLines?.[s.sale_id] || [];
        const lineHit = lines.some((ln) => {
          return (
            String(ln.item_name || "").toLowerCase().includes(q) ||
            String(ln.description || "").toLowerCase().includes(q) ||
            String(ln.item_no || "").toLowerCase().includes(q)
          );
        });

        return headerHit || lineHit;
      });
    },

    sortedSales() {
      return [...this.filteredSales].sort((a, b) => {
        const ad = a?.sale_date ? new Date(a.sale_date).getTime() : 0;
        const bd = b?.sale_date ? new Date(b.sale_date).getTime() : 0;
        if (bd !== ad) return bd - ad;
        return (b.sale_id || 0) - (a.sale_id || 0);
      });
    },

    snapshot() {
      const rows = this.sortedSales;
      const revenue = rows.reduce((s, r) => s + Number(r?.total_sale || 0), 0);
      const cogs = rows.reduce((s, r) => s + Number(r?.total_cogs || 0), 0);
      const net = rows.reduce((s, r) => s + Number(r?.net_profit || 0), 0);
      return { revenue, cogs, net };
    },
  },

  async mounted() {
    this._mq = window.matchMedia("(max-width: 980px)");
    this.isMobile = !!this._mq.matches;
    this._mq.addEventListener?.("change", this.onMQChange);

    await this.refresh();
  },

  beforeUnmount() {
    this._mq?.removeEventListener?.("change", this.onMQChange);
  },

  methods: {
    onMQChange(e) {
      this.isMobile = !!e.matches;
    },

    clearFilters() {
      this.filters.search = "";
      this.filters.platform = "";
      this.filters.shipped = "";
      this.filters.range = "30d";
      this.filters.only_losses = false;
    },

    setRange(key) {
      this.filters.range = key;
    },

    onFilterChange() {
      // local-only filtering
    },

    async refresh() {
      this.loading = true;
      try {
        const res = await api.get("/sales");
        this.sales = Array.isArray(res.data) ? res.data : [];

        const set = new Set(this.sales.map((s) => s.platform).filter(Boolean));
        this.platforms = [...set].sort((a, b) => String(a).localeCompare(String(b)));
      } catch (e) {
        console.error("[SalesPage] fetch failed:", e);
        this.sales = [];
      } finally {
        this.loading = false;
      }
    },

    isExpanded(saleId) {
      return this.expanded.has(saleId);
    },

    async toggleExpand(saleId, saleDate) {
      if (this.expanded.has(saleId)) {
        this.expanded.delete(saleId);
        this.expanded = new Set(this.expanded);
        return;
      }

      this.expanded.add(saleId);
      this.expanded = new Set(this.expanded);

      if (!this.saleLines[saleId]) {
        this.linesLoading = { ...this.linesLoading, [saleId]: true };
        await this.fetchSaleLines(saleId, saleDate);
        this.linesLoading = { ...this.linesLoading, [saleId]: false };
      }
    },

    async fetchSaleLines(saleId, saleDate) {
      try {
        const res = await api.get(`/sale-lines/${saleId}`);
        const rows = Array.isArray(res.data) ? res.data : [];
        const fixed = rows.map((r) => ({ ...r, sale_date: r.sale_date || saleDate || null }));
        this.saleLines = { ...this.saleLines, [saleId]: fixed };
      } catch (e) {
        console.error("[SalesPage] sale lines fetch failed:", e);
        this.saleLines = { ...this.saleLines, [saleId]: [] };
      } finally {
        this.linesLoading = { ...this.linesLoading, [saleId]: false };
      }
    },

    profitHeat(s) {
      const revenue = Number(s?.total_sale || 0);
      const net = Number(s?.net_profit || 0);

      if (revenue <= 0) {
        return { cls: "neutral", label: "—", title: "No revenue on this sale" };
      }

      const margin = net / revenue;

      if (net < 0) {
        return { cls: "loss", label: "💀 Loss", title: `Net negative (${Math.round(margin * 100)}%)` };
      }
      if (margin >= 0.35) {
        return { cls: "hot", label: "🔥 Hot", title: `High margin (${Math.round(margin * 100)}%)` };
      }
      if (margin >= 0.2) {
        return { cls: "good", label: "👍 Good", title: `Good margin (${Math.round(margin * 100)}%)` };
      }
      if (margin >= 0.08) {
        return { cls: "thin", label: "😬 Thin", title: `Thin margin (${Math.round(margin * 100)}%)` };
      }
      return { cls: "meh", label: "🫤 Meh", title: `Low margin (${Math.round(margin * 100)}%)` };
    },

    async copyText(text, msg = "Copied") {
      const val = String(text || "");
      if (!val) return;

      try {
        await navigator.clipboard.writeText(val);
        this.toast = msg;
        setTimeout(() => (this.toast = ""), 900);
      } catch (e) {
        try {
          const el = document.createElement("textarea");
          el.value = val;
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
          this.toast = msg;
          setTimeout(() => (this.toast = ""), 900);
        } catch {
          alert("Could not copy to clipboard.");
        }
      }
    },

    onWizardReview(draft) {
      const d = draft || {};
      const s = d.sale_summary || {};

      this.wizardDraft = {
        sale_date: s.sale_date || d.sale_date || "",
        platform: s.platform || d.platform || "",
        buyer_name: s.buyer_name || d.buyer_name || "",
        buyer_email: s.buyer_email || d.buyer_email || "",
        tracking_number: s.tracking_number || d.tracking_number || "",
        carrier: s.carrier || d.carrier || "",
        total_fees: Number(s.total_fees ?? d.total_fees ?? 0) || 0,
        total_taxes: Number(s.total_taxes ?? d.total_taxes ?? 0) || 0,
        total_freight: Number(s.total_freight ?? d.total_freight ?? 0) || 0,
        items: Array.isArray(d.items) ? d.items : (Array.isArray(d.lines) ? d.lines : []),
      };

      this.reviewOpen = true;
      this.openWizard = false;
    },

    async saveWizardDraft() {
      try {
        this.savingDraft = true;

        const d0 = this.wizardDraft || {};
        const d = d0.sale_summary ? { ...d0.sale_summary, items: d0.items } : d0;
        const items = Array.isArray(d.items) ? d.items : [];

        const payload = {
          sale_date: d.sale_date || null,
          buyer_name: d.buyer_name || null,
          buyer_email: d.buyer_email || null,
          platform: d.platform || null,
          tracking_number: d.tracking_number || null,
          carrier: d.carrier || null,
          total_fees: Number(d.total_fees || 0),
          total_taxes: Number(d.total_taxes || 0),
          total_freight: Number(d.total_freight || 0),
          items: items.map((it) => ({
            item_no: String(it.item_no || "").trim(),
            item_name: String(it.item_name || "").trim(),
            description: it.description ?? null,
            quantity_sold: Number(it.quantity_sold ?? it.quantity ?? 1),
            unit_price: Number(it.unit_price ?? 0),
          })),
        };

        if (!payload.items.length) throw new Error("Draft has no items.");

        await api.post("/sales", payload);

        this.reviewOpen = false;
        this.wizardDraft = {};
        await this.refresh();
      } catch (e) {
        console.error("[SalesPage] saveWizardDraft failed:", e);
        alert(e?.response?.data?.error || e?.message || "Failed to save sale.");
      } finally {
        this.savingDraft = false;
      }
    },

    openTracking(trackingNumber, carrier) {
      const url = this.getTrackingURL(trackingNumber, carrier);
      if (url === "#") {
        alert("❌ Invalid carrier / tracking.");
        return;
      }
      openExternal(url);
    },

    getTrackingURL(trackingNumber, carrier) {
      const c = String(carrier || "").trim().toUpperCase();
      const t = String(trackingNumber || "").trim();
      const carrierUrls = {
        UPS: `https://www.ups.com/track?tracknum=${encodeURIComponent(t)}`,
        FEDEX: `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(t)}`,
        USPS: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(t)}`,
        DHL: `https://www.dhl.com/global-en/home/tracking.html?tracking-id=${encodeURIComponent(t)}`,
      };
      return carrierUrls[c] || "#";
    },

    fmtDate(v) {
      if (!v) return "—";
      try {
        // stable YYYY-MM-DD if parseable
        const d = new Date(v);
        if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
        return String(v);
      } catch {
        return String(v);
      }
    },

    fmtMoney(n) {
      const x = Number(n || 0);
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(x);
    },

    fmtInt(n) {
      const x = Number(n || 0);
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(x);
    },
  },
};
</script>

<style scoped>
/* Layout */
.layout {
  display: grid;
  gap: 14px;
}

/* Cards – match your newer vibe */
.card {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.card-title {
  font-weight: 900;
  font-size: 16px;
}

.micro {
  font-size: 12px;
  font-weight: 700;
}
.subtle {
  opacity: 0.8;
}
.dim {
  opacity: 0.7;
}

.mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
.strong {
  font-weight: 900;
}

/* Filters */
.filters {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr auto auto;
  gap: 10px;
  align-items: center;
}

.input {
  width: 100%;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 800;
  font-size: 14px;
}

.toggle {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  font-weight: 900;
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.95);
  padding: 10px 12px;
  border-radius: 14px;
  white-space: nowrap;
}
.toggle.danger {
  border-color: rgba(180, 60, 60, 0.35);
}

/* Range row + snapshot */
.range-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}

.range-chip {
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: rgba(246, 251, 255, 0.9);
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 900;
  cursor: pointer;
}
.range-chip.on {
  border-color: rgba(40, 134, 113, 0.55);
  box-shadow: 0 6px 16px rgba(40, 134, 113, 0.12);
}

.range-spacer {
  flex: 1;
}

.snapshot {
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  gap: 10px;
  align-items: stretch;
}

.snap {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
}
.snap .k {
  font-weight: 900;
  font-size: 12px;
  opacity: 0.7;
}
.snap .v {
  font-weight: 900;
  font-size: 14px;
  margin-top: 4px;
}

/* Table */
.table-wrap {
  overflow: auto;
}
.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}
.table th,
.table td {
  border: 1px solid rgba(131, 167, 203, 0.55);
  padding: 10px 8px;
  text-align: left;
  background: rgba(255, 255, 255, 0.65);
}
.table thead th {
  position: sticky;
  top: 0;
  background: #eef4fb;
  z-index: 5;
}
.expander-col {
  width: 46px;
}
.expander {
  width: 46px;
  cursor: pointer;
  text-align: center;
  font-weight: 900;
  user-select: none;
}
.center {
  text-align: center;
}

.sale-title {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

/* Chips / badges (InventoryPage vibe) */
.chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  background: rgba(246, 251, 255, 0.9);
}
.chip.sm {
  padding: 5px 9px;
  font-size: 12px;
}
.chip.ghost {
  opacity: 0.8;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  color: white;
}
.badge.ok {
  background: #288671;
}
.badge.no {
  background: #4f6773;
}

/* Heat */
.heat {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  background: rgba(246, 251, 255, 0.9);
}
.heat.hot  { border-color: rgba(255, 140, 0, 0.45); }
.heat.good { border-color: rgba(40, 134, 113, 0.45); }
.heat.thin { border-color: rgba(79, 103, 115, 0.55); }
.heat.meh  { border-color: rgba(131, 167, 203, 0.75); opacity: 0.95; }
.heat.loss { border-color: rgba(180, 60, 60, 0.45); }
.heat.neutral { opacity: 0.75; }

.neg {
  color: #b43c3c;
  font-weight: 900;
}

/* Lines */
.lines {
  padding: 8px 4px;
}
.lines-inner {
  display: grid;
  gap: 10px;
}
.line-row {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 10px;
  padding: 10px 10px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  background: #f6fbff;
}
.line-title {
  font-weight: 900;
}
.line-sub {
  margin-top: 4px;
  font-weight: 800;
  opacity: 0.9;
}
.dot {
  margin: 0 8px;
  opacity: 0.6;
}
.line-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}
.muted {
  opacity: 0.7;
}
.pad {
  padding: 10px;
}

/* Track */
.track-link {
  font-weight: 900;
  text-decoration: none;
}
.track-link:hover {
  text-decoration: underline;
}

/* Mini buttons (InventoryPage-ish) */
.mini {
  border: 1px solid rgba(50, 123, 168, 0.25);
  background: #327ba8;
  color: white;
  font-weight: 900;
  padding: 8px 10px;
  border-radius: 999px;
  cursor: pointer;
}
.mini:hover {
  background: #286c98;
}
.mini.ghost {
  background: #4f6773;
  border-color: rgba(79, 103, 115, 0.25);
}
.mini.ghost:hover {
  background: #3a4a5e;
}

/* Mobile */
.mobile-only {
  display: none;
}

.mobile-list {
  display: grid;
  gap: 12px;
}
.row-card.compact {
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
  cursor: pointer;
}
.row-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}
.chev {
  font-weight: 900;
  font-size: 18px;
  opacity: 0.8;
}

.sale-metrics {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.metric {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
}
.metric .k {
  font-weight: 900;
  font-size: 12px;
  opacity: 0.7;
}
.metric .v {
  font-weight: 900;
  font-size: 14px;
  margin-top: 4px;
}

.btn-row {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.sale-lines {
  margin-top: 12px;
  border-top: 1px solid rgba(176, 196, 222, 0.65);
  padding-top: 12px;
}
.sale-line {
  padding: 10px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  background: #f6fbff;
  margin-bottom: 10px;
}
.ln-title {
  font-weight: 900;
}
.ln-sub {
  margin-top: 4px;
  font-weight: 800;
  opacity: 0.9;
}
.ln-track {
  margin-top: 8px;
}

/* Empty */
.empty {
  text-align: center;
  padding: 24px 14px;
}
.empty-title {
  font-weight: 900;
  font-size: 18px;
}
.empty-sub {
  margin-top: 6px;
  opacity: 0.8;
  font-weight: 800;
}
.empty-actions {
  margin-top: 12px;
  display: flex;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* Modals */
.modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 18px;
}
.modal-body {
  width: min(1100px, 96vw);
  height: min(900px, 92vh);
  border-radius: 18px;
  overflow: hidden;
}

.review {
  width: min(900px, 96vw);
  max-height: 92vh;
  overflow: auto;
  border-radius: 18px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.92);
  padding: 16px;
  box-shadow: 0px 10px 26px rgba(0, 0, 0, 0.25);
}
.review-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}
.review-title {
  font-weight: 900;
  font-size: 18px;
}
.review-grid {
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}
.review-box {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
}
.label {
  font-weight: 900;
  font-size: 12px;
  opacity: 0.7;
}
.value {
  font-weight: 900;
  margin-top: 4px;
}
.review-lines {
  margin-top: 12px;
}
.review-lines-title {
  font-weight: 900;
  margin-bottom: 8px;
}
.review-line {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
  margin-bottom: 10px;
}
.review-actions {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

/* Skeleton */
.skeleton {
  position: relative;
  overflow: hidden;
}
.sk-left,
.sk-right {
  display: grid;
  gap: 8px;
}
.sk-bar {
  height: 12px;
  border-radius: 999px;
  background: rgba(176, 196, 222, 0.55);
}
.w60 { width: 60%; }
.w45 { width: 45%; }
.w70 { width: 70%; }

.skeleton::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: shimmer 1.2s infinite;
}
@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

/* Toast */
.toast {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 10px 12px;
  border-radius: 999px;
  font-weight: 900;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: rgba(246, 251, 255, 0.92);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.18);
}

/* Responsive */
@media (max-width: 980px) {
  .filters {
    grid-template-columns: 1fr;
  }
  .snapshot {
    grid-template-columns: 1fr;
    width: 100%;
  }
  .table {
    min-width: 920px;
  }
  .mobile-only {
    display: block;
  }
}
* {
  box-sizing: border-box;
}
</style>
